// Fulfillment dates for the customer page, the admin order modal and the order slip.
//
// The dates themselves come from shared/fulfillmentDates.ts — the same rule order
// creation checks a submitted date with — on the org's calendar in its time zone,
// whatever zone this browser is in. This file only labels them.
import {
  calendarDateInTimezone,
  deliveryDaysMask,
  getCustomDaysFromBitmask,
  getEditDeadlineForOrder,
  weekdayOfCalendarDate,
} from "@shared/periods";
import { calendarDaysBetween, offeredFulfillmentDates, staffFulfillmentDates } from "@shared/fulfillmentDates";

export function isOrderingAllowed(): boolean {
  return true;
}

export function getOrderingClosedMessage(): string {
  return "";
}

export type FulfillmentDateOption = {
  label: string;
  sublabel: string;
  value: string;
};

export type WindowConfig = {
  enabled?: boolean;
  frequency?: string;
  cutoffHour?: number;
  cutoffMinute?: number;
  customDays?: number;
  /** The account's delivery weekdays (bitmask), from GET /order-window-config/resolved/:accountId. */
  deliveryDays?: number;
  timezone?: string;
  deliveryLeadDays?: number;
  deliveryEnabled?: boolean;
  pickupEnabled?: boolean;
  pickupFollowsDeliverySchedule?: boolean;
  pickupSchedulingEnabled?: boolean;
  pickupAvailableDays?: number;
  pickupBlackoutDates?: string[];
  pickupMaxLeadDays?: number;
  pickupCutoffHour?: number;
  pickupCutoffMinute?: number;
  pickupPrepDays?: number;
  deliveryMaxLeadDays?: number;
  deliveryBlackoutDates?: string[];
};

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function orgToday(config: WindowConfig, now: Date = new Date()): string {
  return calendarDateInTimezone(now, config.timezone || 'America/Toronto');
}

// "Today" / "Tomorrow" / "Yesterday" / the weekday, and "Sep 28", for a calendar date.
function dateOption(date: string, today: string): FulfillmentDateOption {
  const daysAhead = calendarDaysBetween(today, date);
  const label =
    daysAhead === 0 ? 'Today'
    : daysAhead === 1 ? 'Tomorrow'
    : daysAhead === -1 ? 'Yesterday'
    : DAY_NAMES[weekdayOfCalendarDate(date)!];
  const [, month, day] = date.split('-').map(Number);
  return { label, sublabel: `${MONTH_NAMES_SHORT[month - 1]} ${day}`, value: date };
}

/**
 * The weekdays (0=Sun … 6=Sat) the account can have a delivery: the resolved
 * config's deliveryDays, else worked out from frequency + customDays — never
 * Monday by assumption (a weekly or biweekly schedule delivers on its chosen day).
 */
export function getActiveDeliveryDays(config: WindowConfig): number[] {
  return getCustomDaysFromBitmask(config.deliveryDays ?? deliveryDaysMask(config));
}

export function getAvailableFulfillmentDates(
  type: 'delivery' | 'pickup',
  config: WindowConfig,
  options?: { maxDates?: number }
): FulfillmentDateOption[] {
  const now = new Date();
  const today = orgToday(config, now);
  return offeredFulfillmentDates(type, config, now, { maxDates: options?.maxDates ?? 30 })
    .map((date) => dateOption(date, today));
}

/**
 * The dates staff can move an existing order to (the order slip): the schedule's last
 * day before today, then every scheduled day ahead, cutoff passed or not.
 */
export function getStaffFulfillmentDates(
  type: 'delivery' | 'pickup',
  config: WindowConfig,
  options?: { maxDates?: number }
): FulfillmentDateOption[] {
  const now = new Date();
  const today = orgToday(config, now);
  return staffFulfillmentDates(type, config, now, { maxDates: options?.maxDates ?? 10 })
    .map((date) => dateOption(date, today));
}

/** The label for any calendar date, relative to the org's today. */
export function getFulfillmentDateOption(date: string, config: WindowConfig): FulfillmentDateOption {
  return dateOption(date, orgToday(config, new Date()));
}

/** The next delivery date whose cutoff has not passed (within 30 days), and that cutoff instant. */
export function findNextDeliveryCutoff(config: WindowConfig, now: Date = new Date()): { deliveryDate: string; cutoffDate: Date } | null {
  const [deliveryDate] = offeredFulfillmentDates('delivery', config, now, { maxDates: 1, horizonDays: 30 });
  const cutoffDate = deliveryDate ? getEditDeadlineForOrder(deliveryDate, 'delivery', config) : null;
  return deliveryDate && cutoffDate ? { deliveryDate, cutoffDate } : null;
}

function formatTimeLeft(ms: number): string {
  if (ms <= 0) return '0 minutes';
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const plural = (n: number, unit: string) => `${n} ${unit}${n !== 1 ? 's' : ''}`;
  if (days > 0) return `${plural(days, 'day')}, ${plural(hours, 'hour')}, ${plural(minutes, 'minute')}`;
  if (hours > 0) return `${plural(hours, 'hour')}, ${plural(minutes, 'minute')}`;
  return plural(minutes, 'minute');
}

/** Time left to order for a fulfillment date, and that date's weekday. */
export function getCountdownForDate(
  dateStr: string,
  type: 'delivery' | 'pickup',
  config: WindowConfig
): { timeLeft: string; dayName: string } {
  const weekday = weekdayOfCalendarDate(dateStr);
  const cutoff = getEditDeadlineForOrder(dateStr, type, config);
  return {
    timeLeft: formatTimeLeft(cutoff ? cutoff.getTime() - Date.now() : 0),
    dayName: weekday === null ? '' : DAY_NAMES[weekday],
  };
}
